import { Routes, Route, Navigate, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchMyAccess, portalMe } from './api/resources';
import { useAuth } from './context/AuthContext';
import AppShell from './components/shell/AppShell';
import Login from './pages/Login';
import Register from './pages/Register';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import Measurements from './pages/Measurements';
import TailoringItems from './pages/TailoringItems';
import WorkerDetail from './pages/WorkerDetail';
import CustomerOrders from './pages/CustomerOrders';
import ProductionPipeline from './pages/ProductionPipeline';
import Performance from './pages/Performance';
import Supervisor from './pages/Supervisor';
import Salesmen from './pages/Salesmen';
import PortalAccess from './pages/PortalAccess';
import TailoringItemForm from './pages/TailoringItemForm';
import Customers from './pages/Customers';
import Invoices from './pages/Invoices';
import InvoiceWizard from './pages/InvoiceWizard';
import InvoicePreview from './pages/InvoicePreview';
import ViewInvoices from './pages/ViewInvoices';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Workers from './pages/Workers';
import WorkerItemRates from './pages/utility/WorkerItemRates';
import Settlements from './pages/finance/Settlements';
import PortalHome from './pages/portal/PortalHome';
import PortalOrders from './pages/portal/PortalOrders';
import PortalOrder from './pages/portal/PortalOrder';
import PortalPayouts from './pages/portal/PortalPayouts';
import Spinner from './components/ui/Spinner';
import { homeFor, hasPortalGroup, isManager, managerMayOpen } from './utils/roles';

function Protected({ children }) {
  const { token, ready } = useAuth();
  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Loading…" />
      </div>
    );
  }
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { token, ready, role } = useAuth();
  if (!ready) return <Spinner />;
  if (token) return <Navigate to={homeFor(role)} replace />;
  return children;
}

function PortalHomePath() {
  const { data } = useQuery({ queryKey: ['portal-me'], queryFn: portalMe, staleTime: 60_000 });
  if (data === undefined) return null; // wait, rather than bounce twice
  return <Navigate to={data?.isSupervisor ? '/portal/supervisor' : '/portal'} replace />;
}

function RoleHome() {
  const { role } = useAuth();
  if (role === 'WORKER') return <PortalHomePath />;
  return <Navigate to={homeFor(role)} replace />;
}

// Admin / staff screens. The server refuses their APIs to worker logins
// regardless; redirecting just avoids rendering screens that can only fail.
//
// `need` names the Portal Access switch that lets a worker in here too —
// without one, the area stays office-only.
/** Nowhere left to send them: every screen their login has is switched off. */
function NoAccess() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2 px-6 text-center">
      <p className="text-lg font-semibold text-content">Nothing is open to you yet</p>
      <p className="max-w-sm text-sm text-content-subtle">
        Your login has no screens switched on. Ask an admin to give you access on Portal Access.
      </p>
    </div>
  );
}

function StaffArea({ need }) {
  const { role, user, companyId } = useAuth();
  const shell = useOutletContext(); // pass the shell's controls through
  const { pathname } = useLocation();
  const grouped = hasPortalGroup(role);
  // Not the worker portal's /me: that one is closed to the manager desks, and
  // they are held to a group just the same.
  const { data: me, isLoading } = useQuery({
    queryKey: ['my-access', companyId ?? null, user?.id ?? null],
    queryFn: () => fetchMyAccess(companyId),
    enabled: grouped && !!companyId,
    staleTime: 60_000,
  });
  if (!grouped) return <Outlet context={shell} />; // office logins: admin, staff, viewer
  if (isLoading) return null; // the answer decides where they belong

  const home = isManager(role) ? '/invoices' : (me?.isSupervisor ? '/portal/supervisor' : '/portal');

  // A manager's home is the order list, so switching Orders off leaves them
  // with nowhere to be sent — bouncing would land on this same screen for
  // ever. Say so instead.
  const away = (to) => (pathname === to ? <NoAccess /> : <Navigate to={to} replace />);

  // A switch on Portal Access is the whole answer for every grouped login,
  // managers included — the admin turning Workers on for the workshop desk is
  // what opens this door, and the server is held to the same switch.
  if (need) return me?.access?.[need] ? <Outlet context={shell} /> : away(home);

  // No switch guards this area, so it stays office-only. A manager may still
  // open the few paths that were always theirs.
  if (isManager(role) && managerMayOpen(pathname)) return <Outlet context={shell} />;
  return away(home);
}

function WorkerArea() {
  const { role } = useAuth();
  const shell = useOutletContext();
  return role === 'WORKER' ? <Outlet context={shell} /> : <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/" element={<Protected><AppShell /></Protected>}>
        <Route index element={<RoleHome />} />

        <Route element={<StaffArea />}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="measurements" element={<Measurements />} />
          <Route path="tailoring-items" element={<TailoringItems />} />
          {/* The item form is a page, not a dialog — "new" is listed first so
              it is never read as an id. */}
          <Route path="tailoring-items/new" element={<TailoringItemForm />} />
          <Route path="tailoring-items/:id" element={<TailoringItemForm />} />

          <Route path="performance" element={<Performance />} />
          <Route path="salesmen" element={<Salesmen />} />
          <Route path="portal-access" element={<PortalAccess />} />

          <Route path="view/invoices" element={<ViewInvoices />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<Settings />} />
          <Route path="profile" element={<Profile />} />
          <Route path="utility/worker-item-rates" element={<WorkerItemRates />} />
          <Route path="finance/settlements" element={<Settlements />} />
        </Route>

        {/* Office screens a supervisor can be given on Portal Access. */}
        <Route element={<StaffArea need="orders" />}>
          <Route path="invoices" element={<Invoices />} />
          <Route path="invoices/:id" element={<InvoicePreview />} />
        </Route>
        <Route element={<StaffArea need="createOrder" />}>
          <Route path="invoices/new" element={<InvoiceWizard />} />
          <Route path="invoices/:id/edit" element={<InvoiceWizard />} />
        </Route>
        <Route element={<StaffArea need="customers" />}>
          <Route path="customers" element={<Customers />} />
          <Route path="customers/:id/orders" element={<CustomerOrders />} />
        </Route>
        <Route element={<StaffArea need="production" />}>
          <Route path="production" element={<ProductionPipeline />} />
        </Route>
        <Route element={<StaffArea need="workers" />}>
          <Route path="workers" element={<Workers />} />
          <Route path="workers/:id" element={<WorkerDetail />} />
        </Route>

        <Route element={<WorkerArea />}>
          <Route path="portal" element={<PortalHome />} />
          <Route path="portal/orders" element={<PortalOrders />} />
          <Route path="portal/orders/:invoiceId" element={<PortalOrder />} />
          <Route path="portal/payouts" element={<PortalPayouts />} />
          {/* The supervisor's desk: the same screen, now where they work. */}
          <Route path="portal/supervisor" element={<Supervisor />} />
        </Route>

        {/* Available to every signed-in role. */}
        <Route path="change-password" element={<ChangePassword />} />
      </Route>
      <Route path="*" element={<RoleHome />} />
    </Routes>
  );
}
